import React from "react";
import { ClinicCard } from "./ClinicCard";
import {
  getUnavailableProducts,
  type ClinicProductAvailability,
} from "@/hooks/useClinicProductAvailability";
import type { Product } from "@/components/product-step/types";
import { type MarketCountry, DEFAULT_MARKET } from "@/config/market";

// The clinic / availability / product rows come from Supabase queries
// with joined columns whose shape isn't exhaustively typed here. We just
// pass them through to ClinicCard / availability helpers, so loose typing
// is fine — `Record<string, unknown>` is enough to stay strict-eslint
// friendly without a giant cascade of join types.
type ClinicRow = {
  id: string;
  name?: string | null;
  address?: string | null;
  address_short?: string | null;
} & Record<string, unknown>;
type AvailabilityRow = Record<string, unknown>;
type ProductRow = Record<string, unknown>;

type Region =
  | "zh-kanton"
  | "ost"
  | "zentral"
  | "nordwest"
  | "mittelland"
  | "west"
  | "other";

// ZH first, then ZS, NS, Mittelland, Ostschweiz, Westschweiz
const REGION_ORDER: Region[] = [
  "zh-kanton",
  "zentral",
  "nordwest",
  "mittelland",
  "ost",
  "west",
  "other",
];

const GROUPED_LAYOUT_THRESHOLD = 10;

function getRegion(address: string): Region {
  const match = (address || "").match(/\b(\d{4})\b/);
  if (!match) return "other";
  const plz = parseInt(match[1], 10);
  if (plz >= 1000 && plz <= 2999) return "west";
  if (plz >= 3000 && plz <= 3999) return "mittelland";
  if (plz >= 4000 && plz <= 5999) return "nordwest";
  if (plz >= 6000 && plz <= 6999) return "zentral";
  if (plz >= 7000 && plz <= 7999) return "ost";
  if (plz >= 8000 && plz <= 8999) {
    // Schaffhausen (SH) uses 8200–8299 — its own canton, belongs to Ost
    if (plz >= 8200 && plz <= 8299) return "ost";
    return "zh-kanton";
  }
  if (plz >= 9000 && plz <= 9999) return "ost";
  return "other";
}

interface ClinicListProps {
  clinics: ClinicRow[] | undefined;
  /** Active market — region grouping only applies to the Swiss (default) one. */
  market?: MarketCountry;
  selectedClinic: string;
  onClinicChange: (clinicId: string) => void;
  selectedProducts: string[];
  availabilityData: AvailabilityRow[];
  productsData: ProductRow[];
  onProductRemove: (productId: string) => void;
  translations: {
    notAvailable: string;
    removeProduct: string;
    makeAvailable: string;
    unavailableSuffix: string;
    maps: string;
    regionZhKanton: string;
    regionZentralschweiz: string;
    regionNordwestschweiz: string;
    regionMittelland: string;
    regionOstschweiz: string;
    regionWestschweiz: string;
    regionOther: string;
  };
}

export const ClinicList = ({
  clinics,
  market = DEFAULT_MARKET,
  selectedClinic,
  onClinicChange,
  selectedProducts,
  availabilityData,
  productsData,
  onProductRemove,
  translations,
}: ClinicListProps) => {
  const regionLabel: Record<Region, string> = {
    "zh-kanton": translations.regionZhKanton,
    zentral: translations.regionZentralschweiz,
    nordwest: translations.regionNordwestschweiz,
    mittelland: translations.regionMittelland,
    ost: translations.regionOstschweiz,
    west: translations.regionWestschweiz,
    other: translations.regionOther,
  };

  const sorted = React.useMemo(() => {
    if (!clinics) return [];
    return [...clinics].sort((a, b) => {
      const unavailableA = getUnavailableProducts(
        a.id,
        selectedProducts,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (availabilityData || []) as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (productsData || []) as any,
      );
      const unavailableB = getUnavailableProducts(
        b.id,
        selectedProducts,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (availabilityData || []) as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (productsData || []) as any,
      );
      const isAvailableA = unavailableA.length === 0;
      const isAvailableB = unavailableB.length === 0;
      if (isAvailableA === isAvailableB) return 0;
      return isAvailableB ? 1 : -1;
    });
  }, [clinics, selectedProducts, availabilityData, productsData]);

  // Region grouping is built on Swiss postal-code ranges, so it only applies to
  // the Swiss market. Every other market uses the flat grid regardless of count.
  const useGrouped =
    market === "ch" && sorted.length > GROUPED_LAYOUT_THRESHOLD;

  const grouped = React.useMemo(() => {
    if (!useGrouped) return null;
    return sorted.reduce<Record<Region, ClinicRow[]>>(
      (acc, clinic) => {
        const region = getRegion(clinic.address || clinic.address_short || "");
        acc[region].push(clinic);
        return acc;
      },
      {
        "zh-kanton": [],
        ost: [],
        zentral: [],
        nordwest: [],
        mittelland: [],
        west: [],
        other: [],
      },
    );
  }, [sorted, useGrouped]);

  const card = (clinic: ClinicRow) => (
    <ClinicCard
      key={clinic.id}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clinic={clinic as any}
      selectedClinic={selectedClinic}
      selectedProducts={selectedProducts}
      availabilityData={availabilityData}
      productsData={productsData}
      onProductRemove={onProductRemove}
      onClinicSelect={onClinicChange}
      translations={translations}
    />
  );

  // Same responsive grid template for every section so columns align visually.
  // Default grid stretch behaviour keeps cards in the same row equal height;
  // content drives row height (no forced min-height).
  const GRID_CLASS = "grid gap-4 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3";

  // A lone clinic (NL today) would sit in the first grid column with dead space
  // beside it, so it keeps the cell width it would have had and gets centred.
  const SOLO_CLASS = "grid gap-4 mx-auto w-full lg:w-1/2 2xl:w-1/3";

  const flatGrid = (items: ClinicRow[]) => (
    <div className={items.length === 1 ? SOLO_CLASS : GRID_CLASS}>
      {items.map(card)}
    </div>
  );

  if (!useGrouped || !grouped) {
    return flatGrid(sorted);
  }

  // Build render units: standalone sections (>1 clinic) and batches of single-clinic regions
  type StandaloneUnit = {
    type: "standalone";
    region: Region;
    clinics: ClinicRow[];
  };
  type SinglesUnit = {
    type: "singles";
    items: { region: Region; clinic: ClinicRow }[];
  };
  type RenderUnit = StandaloneUnit | SinglesUnit;

  const units: RenderUnit[] = [];
  let batch: { region: Region; clinic: ClinicRow }[] = [];

  const flushBatch = () => {
    if (batch.length > 0) {
      units.push({ type: "singles", items: [...batch] });
      batch = [];
    }
  };

  for (const region of REGION_ORDER) {
    const clinics = grouped[region];
    if (!clinics?.length) continue;
    if (clinics.length === 1) {
      batch.push({ region, clinic: clinics[0] });
      if (batch.length === 3) flushBatch();
    } else {
      flushBatch();
      units.push({ type: "standalone", region, clinics });
    }
  }
  flushBatch();

  const sectionHeader = (label: string) => (
    <h3 className="text-sm font-medium text-neutral-400 uppercase leading-5 tracking-[0.1px] mb-2">
      {label}
    </h3>
  );

  return (
    <div className="space-y-8">
      {units.map((unit, i) => {
        if (unit.type === "standalone") {
          return (
            <div key={unit.region}>
              {sectionHeader(regionLabel[unit.region])}
              {flatGrid(unit.clinics)}
            </div>
          );
        }

        // Singles batch — share one responsive grid with the rest of the page.
        // Each item is a column with its own header + card stretched to row height.
        return (
          <div key={i} className={GRID_CLASS}>
            {unit.items.map(({ region, clinic }) => (
              <div key={region} className="flex flex-col">
                {sectionHeader(regionLabel[region])}
                <div className="flex-1">{card(clinic)}</div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
